import { Injectable, InjectionToken, inject } from '@angular/core';
import {
  AccountInfo,
  AuthenticationResult,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';

import { ciamLoginRequest, ciamMsalConfig } from './auth-config';

// The standalone CIAM PublicClientApplication, provided via a token so it is a DI seam
// (tests supply a fake instead of constructing real MSAL). The factory returns `null`
// when the environment has no CIAM app registration (prod), which disables the rail.
export const CIAM_MSAL_INSTANCE = new InjectionToken<IPublicClientApplication | null>('CIAM_MSAL_INSTANCE', {
  providedIn: 'root',
  factory: () => (ciamMsalConfig ? new PublicClientApplication(ciamMsalConfig) : null),
});

// sessionStorage key naming the rail that owns the current interaction/session.
// Set BEFORE each loginRedirect (so it survives the round-trip) and read on the
// callback return to pick which MSAL instance processes the response, where to
// route, and which instance's token the interceptor attaches. Lives in
// sessionStorage so its lifetime matches the sessionStorage MSAL token cache —
// a fresh tab has neither, keeping the two consistent.
export const SUPPORT_RAIL_KEY = 'bt_support_rail';
export type SupportRail = 'staff' | 'partner';

/**
 * Owns the standalone CIAM (partner/VAR) MSAL instance — deliberately NOT the
 * msal-angular MSAL_INSTANCE (that one is the workforce/staff rail). The two rails
 * have different clientIds AND tenants, so a single instance with per-request
 * authority cannot serve both; a second PublicClientApplication is the only option.
 *
 * `null` config (prod, until the prod CIAM app registration exists) means the partner
 * rail is unavailable: no instance is built and every method is an inert no-op, so the
 * console runs staff-only without special-casing at each call site.
 */
@Injectable({ providedIn: 'root' })
export class CiamAuthService {
  private readonly instance = inject(CIAM_MSAL_INSTANCE);

  /** True when this environment has a CIAM app registration (partner rail enabled). */
  isConfigured(): boolean {
    return this.instance !== null;
  }

  /**
   * MSAL Browser v5 requires initialize() before any other call. Safe to call when
   * unconfigured (no-op). Called once from the app initializer, before guards run.
   */
  async initialize(): Promise<void> {
    if (!this.instance) {
      return;
    }
    await this.instance.initialize();
  }

  /**
   * Process a redirect return on the CIAM instance and activate the returned account.
   * Only invoked by the app initializer when the persisted rail is 'partner', so the
   * workforce instance never races the CIAM instance for the response hash. Mirrors the
   * workforce initializer's contract: on a successful return set the named account
   * active; on a normal (non-redirect) load with no active account, activate a cached
   * one ONLY when unambiguous (exactly one) — never pick arbitrarily among several.
   * A failed/denied return is swallowed (returns null) so app bootstrap is not aborted.
   */
  async handleRedirect(): Promise<AuthenticationResult | null> {
    if (!this.instance) {
      return null;
    }
    let result: AuthenticationResult | null = null;
    try {
      result = await this.instance.handleRedirectPromise();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('CIAM redirect handling failed', err);
      return null;
    }
    if (result?.account) {
      this.instance.setActiveAccount(result.account);
      return result;
    }
    if (!this.instance.getActiveAccount()) {
      const accounts = this.instance.getAllAccounts();
      if (accounts.length === 1) {
        this.instance.setActiveAccount(accounts[0]);
      }
    }
    return null;
  }

  /**
   * Begin CIAM sign-in. Marks the rail 'partner' first so the return leg is routed to
   * this instance (and the interceptor attaches this rail's token). Inert when the
   * partner rail is unconfigured — callers gate the entry point on isConfigured().
   */
  loginRedirect(): void {
    if (!this.instance || !ciamLoginRequest) {
      return;
    }
    sessionStorage.setItem(SUPPORT_RAIL_KEY, 'partner');
    this.instance.loginRedirect(ciamLoginRequest);
  }

  getActiveAccount(): AccountInfo | null {
    return this.instance?.getActiveAccount() ?? null;
  }

  isSignedIn(): boolean {
    return this.getActiveAccount() !== null;
  }

  /**
   * Acquire a CIAM access token for the support API. Prefers the active account, falling
   * back to a cached account ONLY when there is exactly one (never picks arbitrarily
   * among several — that could acquire under the wrong identity). Rejects if the instance
   * is unconfigured or no account resolves, so the interceptor can defer cleanly.
   */
  async acquireToken(): Promise<AuthenticationResult> {
    if (!this.instance || !ciamLoginRequest) {
      throw new Error('CIAM partner rail is not configured');
    }
    const cached = this.instance.getAllAccounts();
    const account = this.instance.getActiveAccount() ?? (cached.length === 1 ? cached[0] : null);
    if (!account) {
      throw new Error('No CIAM account available for token acquisition');
    }
    return this.instance.acquireTokenSilent({ ...ciamLoginRequest, account });
  }

  /** Full sign-out via the CIAM redirect flow. No-op when unconfigured. */
  signOut(): void {
    this.instance?.logoutRedirect();
  }
}
